import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '@hooks/useAuth';
import useWindowWidth from '@hooks/useWindowWidth';
import LoginImg from '@assets/images/login-image.png';
import {
  updateErrors,
  getErrorMessage as getErrorMessageFromErrors,
} from '@utils/commons';
import NewErrorSummary from '@components/NewErrorSummary';
import { FormError, SubmitStatus, SubmitStatusEnum } from '@typings/common';
import getErrorMessage from '@utils/errorUtils';
import validateLoginEmail from './utils';
import './styles.scss';

interface LoginInfo {
  email: string;
  password: '';
}

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loginStatus, setLoginStatus] = useState<SubmitStatus>({
    loading: false,
    submitted: SubmitStatusEnum.IDLE,
    message: '',
  });
  const [fieldErrMessage, setFieldErrMessage] = useState<{
    email: string | undefined;
    password: string | undefined;
  }>({
    email: '',
    password: '',
  });

  const width = useWindowWidth();
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormError[]>([]);
  const [loginInfo, setLoginInfo] = useState<LoginInfo>({
    email: '',
    password: '',
  });

  const onSubmit = async (event: any) => {
    event.preventDefault();

    const { email, password } = loginInfo;

    let validationErrors: FormError[] = [];

    validationErrors = updateErrors(
      validationErrors,
      'email',
      validateLoginEmail(email)
    );
    validationErrors = updateErrors(
      validationErrors,
      'password',
      password.trim() ? '' : 'Please enter your password'
    );

    setErrors(prev => validationErrors);

    if (validationErrors.length !== 0) {
      return;
    }
    setLoginStatus(prev => ({ ...prev, loading: true }));
    try {
      const response = await login(email, password);
      if (response?.status === 'success') {
        setLoginStatus(prev => ({
          ...prev,
          submitted: SubmitStatusEnum.SUCCESS,
          message: '',
        }));
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      console.error(errorMessage);
      setLoginStatus({
        loading: false,
        submitted: SubmitStatusEnum.FAILED,
        message: errorMessage,
      });

      validationErrors = updateErrors(
        validationErrors,
        'loginAPI',
        errorMessage
      );
      setErrors(prev => validationErrors);
    } finally {
      setLoginStatus(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setFieldErrMessage({
      email: getErrorMessageFromErrors(errors, 'email'),
      password: getErrorMessageFromErrors(errors, 'password'),
    });
  }, [errors]);

  const handleInputChange = (field: 'email' | 'password', value: string) => {
    setLoginInfo(prevState => ({ ...prevState, [field]: value }));
  };

  return (
    <div className="govuk-width-container mt-5 login-page mb-5 md:mb-10">
      <h1 className="govuk-heading-l">Welcome to MyWishes Care</h1>
      <div className="login-form govuk-grid-row">
        {width < 641 && (
          <div className="govuk-grid-column-one-half mb-3">
            <img
              src={LoginImg}
              alt="Login illustration"
              className="govuk-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
        <div className="govuk-grid-column-one-half">
          <p className="govuk-body">
            MyWishes Care software is provided to hospices, care homes,
            residential homes and care professionals. If your organisation is
            not currently using MyWishes Care{' '}
            <a
              className="govuk-link"
              href="https://mywishes.care/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              get in touch.
            </a>
          </p>
          <h2 className="govuk-heading-m">Log in to your account</h2>

          <form>
            {errors.length > 0 && <NewErrorSummary errors={errors} />}
            <div
              className={`govuk-form-group ${fieldErrMessage.email ? 'govuk-form-group--error' : ''}`}
            >
              <label className="govuk-label" htmlFor="email">
                Email Address
              </label>
              {fieldErrMessage.email && (
                <span id="email-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {fieldErrMessage.email}
                </span>
              )}

              <input
                className={`govuk-input ${fieldErrMessage.email ? 'govuk-input--error' : ''}`}
                id="email"
                name="email"
                type="email"
                spellCheck={false}
                placeholder="Enter your email address"
                aria-describedby={
                  fieldErrMessage.email ? 'email-input-error' : undefined
                }
                autoComplete="email"
                value={loginInfo.email}
                onChange={e => handleInputChange('email', e.target.value)}
              />
            </div>

            <div
              className={`govuk-password-input ${errors?.find(err => err.id === 'password')?.message ? 'govuk-form-group--error' : ''}`}
              data-module="govuk-password-input"
            >
              <label className="govuk-label" htmlFor="password">
                Password
              </label>
              {fieldErrMessage.password && (
                <span id="password-error" className="govuk-error-message">
                  <span className="govuk-visually-hidden">Error:</span>{' '}
                  {fieldErrMessage.password}
                </span>
              )}
              <div className="govuk-input__wrapper govuk-password-input__wrapper">
                <input
                  className={`govuk-input ${fieldErrMessage.password ? 'govuk-input--error' : ''}`}
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  spellCheck="false"
                  autoComplete="current-password"
                  placeholder="Enter your password"
                  aria-describedby={
                    fieldErrMessage.password ? 'password-error' : undefined
                  }
                  value={loginInfo.password}
                  onChange={e => handleInputChange('password', e.target.value)}
                />
              </div>
            </div>
            <button
              type="button"
              className="govuk-button govuk-button--secondary"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              style={{ marginTop: '0.5rem' }}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>

            <button
              type="submit"
              className="govuk-button govuk-!-width-full login-btn"
              disabled={loginStatus.loading}
              onClick={onSubmit}
            >
              {loginStatus.loading ? 'Logging in...' : 'Login'}
            </button>
            <div className="text-right govuk-!-margin-1 forgot-password-link">
              <a href="/forgot-password" className="govuk-link">
                Forgot your password?
              </a>
            </div>
          </form>
        </div>
        {width >= 641 && (
          <div className="govuk-grid-column-one-half">
            <img
              src={LoginImg}
              alt="Login illustration"
              className="govuk-image"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
